import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ProjectService from "../services/projectService";
import {
  Project,
  Workflow,
  Recording,
  Scrambling,
} from "../declarations/interface";
import flowService from "../services/flowService";
import recordingService from "../services/recordingService";
import scramblingService from "../services/scramblingService";

export const getProjectById: any = createAsyncThunk(
  "project/getProjectById",
  async (id: string) => {
    return ProjectService.getProjectById(id);
  }
);

export const updateWorkflowName: any = createAsyncThunk(
  "project/updateWorkflowName",
  async ({ id, name }: { id: string; name: string }) => {
    return flowService.updateWorkflowName(id, name);
  }
);

export const updateRecordingName: any = createAsyncThunk(
  "project/updateRecordingName",
  async ({ id, name }: { id: string; name: string }) => {
    return recordingService.updateRecordingName(id, name);
  }
);

export const updateProjectName: any = createAsyncThunk(
  "project/updateProjectName",
  async ({ id, name }: { id: string; name: string }) => {
    return ProjectService.updateProjectName(id, name);
  }
);

export const deleteWorkflow: any = createAsyncThunk(
  "project/deleteWorkflow",
  async (id: string) => {
    return flowService.deleteWorkflowById(id);
  }
);

export const deleteRecording: any = createAsyncThunk(
  "project/deleteRecording",
  async (id: string) => {
    return recordingService.deleteRecordingById(id);
  }
);

export const createScrambling: any = createAsyncThunk(
  "project/createScrambling",
  async (scrambling: Scrambling) => {
    return scramblingService.createScrambling(scrambling);
  }
);
export const updateScrambling: any = createAsyncThunk(
  "project/updateScrambling",
  async (scrambling: Scrambling) => {
    return scramblingService.updateScrambling(scrambling);
  }
);
export const deleteScrambling: any = createAsyncThunk(
  "project/deleteScrambling",
  async (id: string) => {
    return scramblingService.deleteScrambling(id);
  }
);

const DEFAULT: {
  project?: Project;
  status: {
    type: "SUCCESS" | "FAILURE" | "ERROR";
    message: string;
  } | null;
} = {
  status: null,
};

const slice = createSlice({
  name: "project",
  initialState: DEFAULT,
  reducers: {
    clearStatus(state) {
      state.status = null;
    },
  },
  extraReducers: {
    [getProjectById.fulfilled]: (state, action) => {
      state.project = action.payload;
    },
    [getProjectById.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while fetching project details",
      };
    },
    [updateWorkflowName.fulfilled]: (state) => {
      state.status = {
        type: "SUCCESS",
        message: "Workflow name updated successfully",
      };
    },
    [updateWorkflowName.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while updating workflow name",
      };
    },
    [updateRecordingName.fulfilled]: (state) => {
      state.status = {
        type: "SUCCESS",
        message: "Recording name updated successfully",
      };
    },
    [updateRecordingName.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while updating recording name",
      };
    },
    [updateProjectName.fulfilled]: (state) => {
      state.status = {
        type: "SUCCESS",
        message: "Project name updated successfully",
      };
    },
    [updateProjectName.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while updating project name",
      };
    },
    [deleteWorkflow.fulfilled]: (state, action) => {
      const { workflow_id } = action.payload;
      if (state.project) {
        state.project.workflow_lists = state.project.workflow_lists.filter(
          (workflow: Workflow) => workflow.workflow_id !== workflow_id
        );
      }
      state.status = {
        type: "SUCCESS",
        message: "Workflow deleted successfully",
      };
    },
    [deleteWorkflow.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while deleting workflow",
      };
    },
    [deleteRecording.fulfilled]: (state, action) => {
      const { recording_id } = action.payload;
      if (state.project) {
        state.project.recordings_lists = state.project.recordings_lists.filter(
          (recording: Recording) => recording.recording_id !== recording_id
        );
      }
      state.status = {
        type: "SUCCESS",
        message: "Recording deleted successfully",
      };
    },
    [deleteRecording.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while deleting scrambling",
      };
    },
    [createScrambling.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while deleting scrambling",
      };
    },
    [createScrambling.fulfilled]: (state, action) => {
      const scrambling = action.payload;
      if (state.project) {
        state.project.scramblings = [...state.project.scramblings, scrambling];
      }
      state.status = {
        type: "SUCCESS",
        message: "Scrambling created successfully",
      };
    },
    [createScrambling.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while creating scrambling",
      };
    },
    [updateScrambling.fulfilled]: (state, action) => {
      const updatedScrambling = action.payload;
      if (state.project) {
        state.project.scramblings = state.project.scramblings.map(
          (scrambling: Scrambling) => {
            if (scrambling.id === updatedScrambling.id) {
              return updatedScrambling;
            }
            return scrambling;
          }
        );
      }
      state.status = {
        type: "SUCCESS",
        message: "Scrambling updated successfully",
      };
    },
    [updateScrambling.rejected]: (state) => {
      state.status = {
        type: "FAILURE",
        message: "Error while updating scrambling",
      };
    },
    [deleteScrambling.fulfilled]: (state, action) => {
      const { id } = action.payload;
      if (state.project) {
        state.project.scramblings = state.project.scramblings.filter(
          (scrambling: Scrambling) => scrambling.id !== id
        );
      }
      state.status = {
        type: "SUCCESS",
        message: "Scrambling deleted successfully",
      };
    },
  },
});

export const reducer = slice.reducer;
export const actions = slice.actions;

export default slice;
